import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, FileText, Building2, GraduationCap, DollarSign,
  CheckSquare, BarChart3, Users as UsersIcon, Settings, Search, LogOut, Menu, X, BookOpenCheck,
  UserRound, Files, School, Layers, Upload, LineChart, Merge, History, Plane, Briefcase, ClipboardList,
  ChevronDown, PhoneCall, Inbox, Zap, ShieldCheck, FileDown } from "lucide-react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import NotificationsBell from "./NotificationsBell";

const B2B_NAV = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", lob: "B2B" },
  { to: "/my-work", icon: CheckSquare, label: "My Work", lob: "B2B", roles: ["admin","manager","management","bdm","admissions_officer"] },
  { to: "/students", icon: UserRound, label: "Students", lob: "B2B" },
  { to: "/applications", icon: FileText, label: "Applications", lob: "B2B" },
  { to: "/partners", icon: Building2, label: "Partners", lob: "B2B" },
  { to: "/enrollment", icon: BookOpenCheck, label: "Enrollment", lob: "B2B" },
  { to: "/commissions", icon: DollarSign, label: "Commissions", lob: "B2B", roles: ["admin","finance","management","manager"] },
  { to: "/work-report", icon: FileDown, label: "Work Report", lob: "B2B", roles: ["bdm","admissions_officer"] },
];

const B2C_NAV = [
  { to: "/b2c", icon: LayoutDashboard, label: "Dashboard", lob: "B2C" },
  { to: "/b2c/my-work", icon: CheckSquare, label: "My Work", lob: "B2C", roles: ["admin","manager","admissions_officer"] },
  { to: "/b2c/students", icon: UserRound, label: "Students", lob: "B2C" },
  { to: "/b2c/duplicates", icon: Merge, label: "Merge Duplicates", lob: "B2C", roles: ["admin","manager","admissions_officer"] },
  { to: "/b2c/applications", icon: Files, label: "Applications", lob: "B2C" },
  { to: "/b2c/enrollment", icon: BookOpenCheck, label: "Enrollment", lob: "B2C" },
  { to: "/b2c/commissions", icon: DollarSign, label: "Commissions", lob: "B2C", roles: ["admin","manager","finance","management"] },
  { to: "/b2c/variance", icon: LineChart, label: "Variance Report", lob: "B2C", roles: ["admin","manager","finance","management"] },
  { to: "/b2c/import", icon: Upload, label: "Bulk Import", lob: "B2C", roles: ["admin","manager","admissions_officer"] },
];

const IMM_NAV = [
  { to: "/immigration", icon: ClipboardList, label: "My Files", roles: ["filing_officer","filing_manager","admin","manager"] },
  { to: "/immigration/my-tasks", icon: CheckSquare, label: "My Tasks", roles: ["filing_officer","filing_manager","admin","manager","sales"] },
  { to: "/immigration/filing-manager", icon: LayoutDashboard, label: "Filing Manager", roles: ["admin","manager","filing_manager","management"] },
  { to: "/immigration/applications", icon: Plane, label: "Applications", roles: ["admin","manager","filing_manager","filing_officer","sales","management"] },
  { to: "/immigration/new", icon: Briefcase, label: "New Application", roles: ["admin","manager","filing_manager","sales"] },
  { to: "/immigration/ceo", icon: BarChart3, label: "CEO Dashboard", roles: ["admin","manager","management","filing_manager"] },
  { to: "/immigration/performance", icon: LineChart, label: "Performance", roles: ["admin","manager","filing_manager","management"] },
  { to: "/immigration/app-types", icon: Layers, label: "Application Types", roles: ["admin","manager"] },
  { to: "/immigration/checklist-templates", icon: ClipboardList, label: "Checklist Templates", roles: ["admin","manager"] },
  { to: "/immigration/fees", icon: DollarSign, label: "Fees Master Data", roles: ["admin","manager"] },
  { to: "/immigration/ircc-forms", icon: FileText, label: "IRCC Forms", roles: ["admin","manager"] },
  { to: "/immigration/retainer-settings", icon: ShieldCheck, label: "Retainer & RCIC", roles: ["admin","manager"] },
  { to: "/immigration/retainer-clauses", icon: ClipboardList, label: "Retainer Clauses", roles: ["admin","manager"] },
];

const LEADS_NAV = [
  { to: "/leads", icon: Inbox, label: "All Leads", roles: ["admin","manager","management","filing_manager","smm","reception","sales","admissions_officer","bdm"] },
  { to: "/leads/new", icon: PhoneCall, label: "New Lead", roles: ["admin","manager","management","filing_manager","smm","reception","sales","admissions_officer","bdm"] },
  { to: "/leads/mine", icon: CheckSquare, label: "My Leads" },
  { to: "/leads/reporting", icon: BarChart3, label: "Lead Reporting", roles: ["admin","manager","management","filing_manager"] },
  { to: "/leads/webhook-setup", icon: Zap, label: "Meta Lead Sync", roles: ["admin","manager"] },
];

const COMMON_NAV = [
  { to: "/clients", icon: UserRound, label: "Client 360", lob: "B2C" },
  { to: "/tasks", icon: CheckSquare, label: "Tasks", lob: "B2B" },
  { to: "/reports", icon: BarChart3, label: "Reports", lob: "B2B" },
  { to: "/master-data", icon: School, label: "Master Data", roles: ["admin","manager"] },
  { to: "/audit-log", icon: History, label: "Audit Log", roles: ["admin","manager"] },
  { to: "/users", icon: UsersIcon, label: "Users", roles: ["admin"] },
  { to: "/settings", icon: Settings, label: "Settings" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  // Collapsible sidebar sections (persisted per user)
  const COLLAPSE_KEY = "admissio_sidebar_collapsed_v1";
  const [collapsed, setCollapsed] = useState(() => {
    try { return JSON.parse(localStorage.getItem(COLLAPSE_KEY) || "{}"); } catch { return {}; }
  });
  useEffect(() => { try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify(collapsed)); } catch {} }, [collapsed]);
  const toggle = (k) => setCollapsed(c => ({ ...c, [k]: !c[k] }));
  const isCollapsed = (k) => !!collapsed[k];

  const runSearch = async (v) => {
    setQ(v);
    if (!v.trim()) { setResults(null); return; }
    try {
      const r = await api.get(`/search?q=${encodeURIComponent(v)}`);
      setResults(r.data);
    } catch { setResults(null); }
  };

  const lob = (user?.line_of_business || "BOTH").toUpperCase();
  const isAdmin = user?.role === "admin";
  const canSeeLob = (l) => isAdmin || lob === "BOTH" || lob === l;
  const roleAllow = (item) => !item.roles || item.roles.includes(user?.role);
  // LOB filter — items without a `lob` key are visible to everyone, items with
  // lob="B2B"/"B2C" are hidden from users of the opposite LOB (admins + BOTH see all).
  const lobAllow = (item) => !item.lob || canSeeLob(item.lob);
  const navAllow = (item) => roleAllow(item) && lobAllow(item);
  const link = (item) => (
    <NavLink key={item.to} to={item.to}
      data-testid={`nav-${item.label.toLowerCase().replace(/ /g,'-')}`}
      onClick={() => setOpen(false)}
      end={item.to === "/b2c" || item.to === "/dashboard"}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
          isActive ? "bg-orange-500/15 text-white border-l-2 border-orange-500 pl-[10px]"
                   : "text-slate-300 hover:bg-white/5 hover:text-white"
        }`}>
      <item.icon className="w-4 h-4" /> {item.label}
    </NavLink>
  );

  const SectionHeader = ({ id, label, count }) => (
    <button
      type="button"
      data-testid={`nav-section-toggle-${id}`}
      onClick={() => toggle(id)}
      className="w-full flex items-center justify-between px-3 pt-4 pb-1 text-[10px] uppercase tracking-widest text-slate-500 font-semibold hover:text-slate-300 transition-colors">
      <span>{label} <span className="text-slate-600 normal-case tracking-normal">({count})</span></span>
      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isCollapsed(id) ? "-rotate-90" : ""}`} />
    </button>
  );

  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside data-testid="app-sidebar"
             className={`fixed lg:static inset-y-0 left-0 z-40 w-64 shrink-0 text-slate-200 flex flex-col
                         transform ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0
                         transition-transform`}
             style={{ background: "#0B192C" }}>
        <div className="h-16 px-5 flex items-center gap-3 border-b border-white/10 shrink-0" style={{background:"#0B192C"}}>
          <div className="w-9 h-9 rounded-lg bg-orange-500 flex items-center justify-center text-white">
            <GraduationCap className="w-5 h-5" />
          </div>
          <div>
            <div className="heading-font text-lg font-bold text-white leading-tight">Admissio</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400">Admissions Suite</div>
          </div>
        </div>
        <nav data-testid="app-sidebar-nav" className="p-3 space-y-1 flex-1 overflow-y-auto">
          {canSeeLob("B2B") && <>
            <SectionHeader id="b2b" label="B2B" count={B2B_NAV.filter(roleAllow).length} />
            {!isCollapsed("b2b") && B2B_NAV.filter(roleAllow).map(link)}
          </>}
          {canSeeLob("B2C") && <>
            <SectionHeader id="b2c" label="B2C" count={B2C_NAV.filter(roleAllow).length} />
            {!isCollapsed("b2c") && B2C_NAV.filter(roleAllow).map(link)}
          </>}
          {IMM_NAV.filter(roleAllow).length > 0 && <>
            <div data-testid="nav-section-immigration">
              <SectionHeader id="immigration" label="Immigration" count={IMM_NAV.filter(roleAllow).length} />
            </div>
            {!isCollapsed("immigration") && IMM_NAV.filter(roleAllow).map(link)}
          </>}
          {LEADS_NAV.filter(roleAllow).length > 0 && <>
            <div data-testid="nav-section-lead-management">
              <SectionHeader id="leads" label="Lead Management" count={LEADS_NAV.filter(roleAllow).length} />
            </div>
            {!isCollapsed("leads") && LEADS_NAV.filter(roleAllow).map(link)}
          </>}
          <SectionHeader id="common" label="Common" count={COMMON_NAV.filter(navAllow).length} />
          {!isCollapsed("common") && COMMON_NAV.filter(navAllow).map(link)}
        </nav>
        <div className="p-3 shrink-0 border-t border-white/10">
          <div className="p-3 rounded-lg bg-white/5 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-orange-500/90 flex items-center justify-center text-white text-sm font-semibold">
              {user?.name?.[0] || "?"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white truncate">{user?.name}</div>
              <div className="text-[10px] text-slate-400 capitalize">{user?.role?.replace(/_/g," ")} · {lob}</div>
            </div>
            <button data-testid="logout-btn" onClick={logout} className="text-slate-400 hover:text-white">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center gap-4 px-4 sm:px-6 sticky top-0 z-30">
          <button className="lg:hidden" onClick={() => setOpen(true)}><Menu className="w-5 h-5" /></button>
          <div className="relative flex-1 max-w-xl">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              data-testid="global-search-input"
              value={q}
              onChange={(e) => runSearch(e.target.value)}
              placeholder="Search student, phone, email, app ID, partner…"
              className="w-full h-10 pl-10 pr-3 rounded-lg border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:border-orange-400 focus:bg-white"
            />
            {results && (results.applications.length + results.partners.length + (results.b2c_applications?.length||0) + (results.students?.length||0) > 0) && (
              <div className="absolute mt-2 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-[500px] overflow-auto z-40">
                {results.applications.length > 0 && <div className="px-4 py-1 text-[10px] uppercase text-slate-500 bg-slate-50 font-semibold">B2B Applications</div>}
                {results.applications.map(a => (
                  <button key={a.id}
                    onClick={() => { setResults(null); setQ(""); nav(`/applications/${a.id}`); }}
                    className="w-full text-left px-4 py-2 hover:bg-slate-50 border-b border-slate-100">
                    <div className="text-sm font-medium text-slate-900">{a.student_name}</div>
                    <div className="text-xs text-slate-500">{a.application_id} · {a.college} · {a.stage}</div>
                  </button>
                ))}
                {results.partners.length > 0 && <div className="px-4 py-1 text-[10px] uppercase text-slate-500 bg-slate-50 font-semibold">B2B Partners</div>}
                {results.partners.map(p => (
                  <button key={p.id}
                    onClick={() => { setResults(null); setQ(""); nav(`/partners/${p.id}`); }}
                    className="w-full text-left px-4 py-2 hover:bg-slate-50 border-b border-slate-100">
                    <div className="text-sm font-medium text-slate-900">{p.company_name}</div>
                    <div className="text-xs text-slate-500">Partner · {p.city}, {p.country}</div>
                  </button>
                ))}
                {(results.students?.length||0) > 0 && <div className="px-4 py-1 text-[10px] uppercase text-slate-500 bg-slate-50 font-semibold">B2C Students</div>}
                {(results.students||[]).map(s => (
                  <button key={s.id}
                    onClick={() => { setResults(null); setQ(""); nav(`/b2c/students/${s.id}`); }}
                    className="w-full text-left px-4 py-2 hover:bg-slate-50 border-b border-slate-100">
                    <div className="text-sm font-medium text-slate-900">{s.full_name}</div>
                    <div className="text-xs text-slate-500">{s.student_id} · {s.email} · UCI {s.uci||"—"}</div>
                  </button>
                ))}
                {(results.b2c_applications?.length||0) > 0 && <div className="px-4 py-1 text-[10px] uppercase text-slate-500 bg-slate-50 font-semibold">B2C Applications</div>}
                {(results.b2c_applications||[]).map(a => (
                  <button key={a.id}
                    onClick={() => { setResults(null); setQ(""); nav(`/b2c/applications/${a.id}`); }}
                    className="w-full text-left px-4 py-2 hover:bg-slate-50 border-b border-slate-100">
                    <div className="text-sm font-medium text-slate-900">{a.student?.full_name || "—"}</div>
                    <div className="text-xs text-slate-500">{a.application_id} · {a.stage} · Filing: {a.filing_status || "—"}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <NotificationsBell />
          {open && <button className="lg:hidden" onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>}
        </header>
        <main className="flex-1 min-w-0 p-6 sm:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
